import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Calendar, Save, RotateCcw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Factory defaults for the appointment workflow toggles. Keep in sync with the
// initial values in the useState below and with the install/setup defaults.
const DEFAULT_APPOINTMENT_SETTINGS = {
  auto_approve_appointments: 'false',
  allow_employee_approval: 'false',
  allow_employee_selection: 'true',
  allow_employee_groomer_reassign: 'false'
};

const AppointmentSettingsEditor = () => {
  const [settings, setSettings] = useState({ ...DEFAULT_APPOINTMENT_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [autoApproveRes, employeeApprovalRes, employeeSelectionRes, employeeReassignRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/auto_approve_appointments`, config).catch(() => ({ data: { info_value: 'false' } })),
        axios.get(`${API_URL}/business-info/allow_employee_approval`, config).catch(() => ({ data: { info_value: 'false' } })),
        axios.get(`${API_URL}/business-info/allow_employee_selection`, config).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/allow_employee_groomer_reassign`, config).catch(() => ({ data: { info_value: 'false' } })),
      ]);

      setSettings({
        auto_approve_appointments: autoApproveRes.data.info_value || 'false',
        allow_employee_approval: employeeApprovalRes.data.info_value || 'false',
        allow_employee_selection: employeeSelectionRes.data.info_value ?? 'true',
        allow_employee_groomer_reassign: employeeReassignRes.data.info_value || 'false'
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to load appointment settings:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  // Persist a settings object via the four business-info PUT endpoints. Used by
  // both Save (current state) and Restore Defaults (DEFAULT_APPOINTMENT_SETTINGS).
  const persistSettings = async (values) => {
    const token = sessionStorage.getItem('token');
    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    await Promise.all([
      axios.put(`${API_URL}/business-info/auto_approve_appointments`, {
        value: values.auto_approve_appointments,
      }, config),
      axios.put(`${API_URL}/business-info/allow_employee_approval`, {
        value: values.allow_employee_approval,
      }, config),
      axios.put(`${API_URL}/business-info/allow_employee_selection`, {
        value: values.allow_employee_selection,
      }, config),
      axios.put(`${API_URL}/business-info/allow_employee_groomer_reassign`, {
        value: values.allow_employee_groomer_reassign,
      }, config),
    ]);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await persistSettings(settings);
      toast.success('Appointment settings saved successfully');
      window.dispatchEvent(new Event('appointmentSettingsUpdated'));
    } catch (error) {
      console.error('Failed to save appointment settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm(
      'Restore appointment settings to factory defaults?\n\n' +
      'This will reset:\n' +
      '\u2022 Auto-Approve Appointments \u2192 OFF\n' +
      '\u2022 Allow Employee Self-Approval \u2192 OFF\n' +
      '\u2022 Allow Employee Selection During Booking \u2192 ON\n' +
      '\u2022 Allow Employees to Reassign Appointments \u2192 OFF\n\n' +
      'Your appointments, services, customers, employees, payments, and other settings are NOT affected.'
    )) {
      return;
    }
    setRestoring(true);
    try {
      const defaults = { ...DEFAULT_APPOINTMENT_SETTINGS };
      await persistSettings(defaults);
      setSettings(defaults);
      toast.success('Appointment settings restored to defaults');
      window.dispatchEvent(new Event('appointmentSettingsUpdated'));
    } catch (error) {
      console.error('Failed to restore appointment settings:', error);
      toast.error('Failed to restore defaults: ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <Calendar className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Appointment Settings</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Configure how appointments are handled in your system, including approval workflows and employee selection.
      </p>

      {/* Settings */}
      <div className="space-y-6">
        {/* Auto-Approve Appointments */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="auto_approve"
              checked={settings.auto_approve_appointments === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                auto_approve_appointments: e.target.checked ? 'true' : 'false' 
              }))}
              className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <div className="flex-1">
              <label htmlFor="auto_approve" className="font-semibold text-gray-900 cursor-pointer block">
                Auto-Approve Appointments
              </label>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, all new appointments are automatically approved. When disabled, appointments require manual admin approval before they're confirmed.
              </p>
            </div>
          </div>
        </div>

        {/* Allow Employee Approval */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="employee_approval"
              checked={settings.allow_employee_approval === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                allow_employee_approval: e.target.checked ? 'true' : 'false' 
              }))}
              disabled={settings.auto_approve_appointments === 'true'}
              className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
            />
            <div className="flex-1">
              <label htmlFor="employee_approval" className="font-semibold text-gray-900 cursor-pointer block">
                Allow Employee Self-Approval
              </label>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, employees can approve their own assigned appointments. Only applies when auto-approve is disabled.
              </p>
              {settings.auto_approve_appointments === 'true' && (
                <p className="text-xs text-yellow-600 mt-2">
                  ⚠️ This setting is disabled because auto-approve is enabled
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Allow Employee Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="employee_selection"
              checked={settings.allow_employee_selection === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                allow_employee_selection: e.target.checked ? 'true' : 'false' 
              }))}
              className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <div className="flex-1">
              <label htmlFor="employee_selection" className="font-semibold text-gray-900 cursor-pointer block">
                Allow Employee Selection During Booking
              </label>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, clients and staff can choose a specific employee when booking. When disabled, the employee selector is hidden and appointments are assigned automatically or by admin.
              </p>
            </div>
          </div>
        </div>

        {/* Allow Employee Groomer Reassignment */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="employee_groomer_reassign"
              checked={settings.allow_employee_groomer_reassign === 'true'}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                allow_employee_groomer_reassign: e.target.checked ? 'true' : 'false' 
              }))}
              className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <div className="flex-1">
              <label htmlFor="employee_groomer_reassign" className="font-semibold text-gray-900 cursor-pointer block">
                Allow Employees to Reassign Appointments
              </label>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, employees can reassign a different employee to appointments. When disabled, only admins can reassign. Admins can always reassign regardless of this setting.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={handleSaveSettings}
          disabled={saving || restoring}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Appointment Settings'}</span>
        </button>
        <button
          onClick={handleRestoreDefaults}
          disabled={saving || restoring}
          className="btn btn-secondary flex items-center space-x-2"
          title="Reset these four toggles to factory defaults. Does not touch appointments, services, customers, payments, or other settings."
        >
          <RotateCcw size={18} />
          <span>{restoring ? 'Restoring...' : 'Restore Defaults'}</span>
        </button>
        <p className="text-xs text-gray-500">
          Restore Defaults only resets the four toggles above. Your data and other settings are untouched.
        </p>
      </div>
    </div>
  );
};

export default AppointmentSettingsEditor;
